import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { pagos, cuentas, categorias } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { exportPagosToPDF } from '../lib/pdfExport';
import { 
  CreditCard, 
  Plus, 
  Edit2, 
  Trash2, 
  AlertTriangle,
  Clock,
  CheckCircle,
  Calendar,
  Loader2,
  Save,
  FileDown,
  Upload,
  FileSpreadsheet,
  Archive,
  Repeat
} from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr + 'T00:00:00');
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const getStatusConfig = (estatus) => {
  const config = {
    pendiente: { label: 'Pendiente', class: 'badge-pendiente', icon: Clock },
    programado: { label: 'Programado', class: 'badge-programado', icon: Calendar },
    pagado: { label: 'Pagado', class: 'badge-pagado', icon: CheckCircle },
    vencido: { label: 'Vencido', class: 'badge-vencido', icon: AlertTriangle }
  };
  return config[estatus] || config.pendiente;
};

export default function Pagos() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pagosList, setPagosList] = useState([]);
  const [cuentasList, setCuentasList] = useState([]);
  const [categoriasList, setCategoriasList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  
  // Period filter
  const [selectedPeriodo, setSelectedPeriodo] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  // Upload period selection
  const [uploadPeriodo, setUploadPeriodo] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [uploadCuentaId, setUploadCuentaId] = useState('');

  const [formData, setFormData] = useState({
    proveedor: '',
    concepto: '',
    monto: '',
    fecha_vencimiento: '',
    categoria_id: '',
    cuenta_id: '',
    estatus: 'pendiente',
    prioridad: 4,
    tipo_recurrencia: 'unico',
    fecha_fin_recurrencia: ''
  });

  const [editingId, setEditingId] = useState(null);
  const [filterEstatus, setFilterEstatus] = useState('all');
  const [filterCuentaId, setFilterCuentaId] = useState('all');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadPagos();
  }, [filterEstatus, selectedPeriodo, filterCuentaId]);

  const loadInitialData = async () => {
    try {
      const [cuentasRes, categoriasRes] = await Promise.all([
        cuentas.getAll(),
        categorias.getAll()
      ]);
      setCuentasList(cuentasRes.data);
      setCategoriasList(categoriasRes.data);
      if (cuentasRes.data.length > 0) {
        setUploadCuentaId(cuentasRes.data[0].id);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadPagos = async () => {
    setLoading(true);
    try {
      // First, generate any recurring payments for this period
      if (selectedPeriodo) {
        try {
          await pagos.generarRecurrentes(selectedPeriodo);
        } catch (e) {
          // Silently fail - recurring generation is a nice-to-have
          console.log('Recurring payment generation skipped');
        }
      }
      
      const params = {};
      if (filterEstatus && filterEstatus !== 'all') params.estatus = filterEstatus;
      if (filterCuentaId && filterCuentaId !== 'all') params.cuenta_id = filterCuentaId;
      if (selectedPeriodo) params.periodo = selectedPeriodo;
      const response = await pagos.getAll(params);
      setPagosList(response.data);
    } catch (error) {
      console.error('Error loading payments:', error);
      toast.error('Error al cargar pagos');
    } finally {
      setLoading(false);
    }
  };

  // Generate period options (last 12 months + next 3)
  const periodoOptions = React.useMemo(() => {
    const periods = [];
    const now = new Date();
    for (let i = -3; i <= 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('es-MX', { year: 'numeric', month: 'long' });
      periods.push({ value, label });
    }
    return periods;
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!uploadCuentaId) {
      toast.error('Selecciona una cuenta primero');
      return;
    }
    
    setUploading(true);
    try {
      const response = await pagos.upload(file, uploadCuentaId, uploadPeriodo);
      toast.success(`${response.data.created} pagos importados correctamente`);
      setUploadDialogOpen(false);
      loadPagos();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar el archivo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleArchivePeriod = async () => {
    if (!window.confirm(`¿Archivar todos los pagos del período ${selectedPeriodo}? Los pagos archivados no se mostrarán en la lista principal.`)) return;
    
    try {
      await pagos.archivePeriod(selectedPeriodo);
      toast.success('Período archivado correctamente');
      loadPagos();
    } catch (error) {
      toast.error('Error al archivar período');
    }
  };

  const handleSave = async () => {
    if (!formData.proveedor || !formData.monto || !formData.fecha_vencimiento || !formData.cuenta_id) {
      toast.error('Completa todos los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        monto: parseFloat(formData.monto)
      };

      if (editingId) {
        await pagos.update(editingId, data);
        toast.success('Pago actualizado');
      } else {
        await pagos.create(data);
        toast.success('Pago creado');
      }
      loadPagos();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Error al guardar pago');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este pago?')) return;
    
    try {
      await pagos.delete(id);
      toast.success('Pago eliminado');
      loadPagos();
    } catch (error) {
      toast.error('Error al eliminar pago');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await pagos.update(id, { estatus: newStatus });
      toast.success('Estado actualizado');
      loadPagos();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const resetForm = () => {
    setFormData({
      proveedor: '',
      concepto: '',
      monto: '',
      fecha_vencimiento: '',
      categoria_id: '',
      cuenta_id: '',
      estatus: 'pendiente',
      prioridad: 4,
      tipo_recurrencia: 'unico',
      fecha_fin_recurrencia: ''
    });
    setEditingId(null);
  };

  const openEdit = (pago) => {
    setFormData({
      proveedor: pago.proveedor,
      concepto: pago.concepto,
      monto: pago.monto.toString(),
      fecha_vencimiento: pago.fecha_vencimiento,
      categoria_id: pago.categoria_id || '',
      cuenta_id: pago.cuenta_id,
      estatus: pago.estatus,
      prioridad: pago.prioridad,
      tipo_recurrencia: pago.tipo_recurrencia || 'unico',
      fecha_fin_recurrencia: pago.fecha_fin_recurrencia || ''
    });
    setEditingId(pago.id);
    setDialogOpen(true);
  };

  const totalPendiente = pagosList
    .filter(p => p.estatus !== 'pagado')
    .reduce((sum, p) => sum + p.monto, 0);

  const totalVencido = pagosList
    .filter(p => p.estatus === 'vencido')
    .reduce((sum, p) => sum + p.monto, 0);

  const handleExportPDF = () => {
    const totales = {
      pendiente: pagosList.filter(p => p.estatus === 'pendiente').reduce((s, p) => s + p.monto, 0),
      programado: pagosList.filter(p => p.estatus === 'programado').reduce((s, p) => s + p.monto, 0),
      vencido: totalVencido,
      total: totalPendiente
    };
    exportPagosToPDF(pagosList.filter(p => p.estatus !== 'pagado'), totales);
    toast.success('PDF generado exitosamente');
  };

  return (
    <div data-testid="pagos-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-mobile">
          <div>
            <h1 style={{ fontFamily: 'Outfit, sans-serif' }}>
              Gestión de Pagos
            </h1>
            <p className="hidden sm:block">
              Administra los pagos pendientes por realizar
            </p>
          </div>
          <div className="page-header-actions">
            <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn" className="btn-icon-only sm:w-auto">
              <FileDown className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">PDF</span>
            </Button>
            {isAdmin && (
              <>
                <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="upload-excel-btn">
                      <Upload className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Cargar Excel</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="w-5 h-5" />
                        Cargar Pagos desde Excel
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Período *</Label>
                        <Select value={uploadPeriodo} onValueChange={setUploadPeriodo}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {periodoOptions.map((p) => (
                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Los pagos se asociarán a este período. Cada mes nuevo, carga un Excel limpio.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Cuenta *</Label>
                        <Select value={uploadCuentaId} onValueChange={setUploadCuentaId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar cuenta" />
                          </SelectTrigger>
                          <SelectContent>
                            {cuentasList.map((cuenta) => (
                              <SelectItem key={cuenta.id} value={cuenta.id}>{cuenta.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="p-4 border-2 border-dashed border-gray-200 rounded-lg text-center">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="pagos-file-upload"
                        />
                        <label htmlFor="pagos-file-upload" className="cursor-pointer">
                          <FileSpreadsheet className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm font-medium">Click para seleccionar archivo</p>
                          <p className="text-xs text-muted-foreground mt-1">Excel (.xlsx, .xls)</p>
                        </label>
                      </div>
                      {uploading && (
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Procesando archivo...
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground bg-gray-50 p-3 rounded">
                        <p className="font-medium mb-1">Columnas esperadas:</p>
                        <code>proveedor, concepto, monto, fecha_vencimiento</code>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={resetForm} data-testid="add-pago-btn" className="flex-1 sm:flex-none">
                      <Plus className="w-4 h-4 mr-2" />
                      Nuevo
                    </Button>
                  </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {editingId ? 'Editar Pago' : 'Nuevo Pago'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Proveedor *</Label>
                      <Input
                        value={formData.proveedor}
                        onChange={(e) => setFormData({ ...formData, proveedor: e.target.value })}
                        placeholder="Nombre del proveedor"
                        data-testid="pago-proveedor"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Monto *</Label>
                      <Input
                        type="number"
                        value={formData.monto}
                        onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                        placeholder="0.00"
                        data-testid="pago-monto"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Concepto</Label>
                    <Input
                      value={formData.concepto}
                      onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
                      placeholder="Descripción del pago"
                      data-testid="pago-concepto"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fecha Vencimiento *</Label>
                      <Input
                        type="date"
                        value={formData.fecha_vencimiento}
                        onChange={(e) => setFormData({ ...formData, fecha_vencimiento: e.target.value })}
                        data-testid="pago-fecha"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cuenta *</Label>
                      <Select 
                        value={formData.cuenta_id} 
                        onValueChange={(v) => setFormData({ ...formData, cuenta_id: v })}
                      >
                        <SelectTrigger data-testid="pago-cuenta">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {cuentasList.map((cuenta) => (
                            <SelectItem key={cuenta.id} value={cuenta.id}>
                              {cuenta.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Categoría</Label>
                      <Select 
                        value={formData.categoria_id} 
                        onValueChange={(v) => setFormData({ ...formData, categoria_id: v })}
                      >
                        <SelectTrigger data-testid="pago-categoria">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoriasList.filter(c => c.activa).map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: cat.color }}
                                />
                                {cat.nombre}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Prioridad</Label>
                      <Select 
                        value={formData.prioridad.toString()} 
                        onValueChange={(v) => setFormData({ ...formData, prioridad: parseInt(v) })}
                      >
                        <SelectTrigger data-testid="pago-prioridad">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-red-500" />
                              Prioridad 1 (Alta)
                            </div>
                          </SelectItem>
                          <SelectItem value="2">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-orange-500" />
                              Prioridad 2
                            </div>
                          </SelectItem>
                          <SelectItem value="3">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-yellow-500" />
                              Prioridad 3
                            </div>
                          </SelectItem>
                          <SelectItem value="4">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-green-500" />
                              Prioridad 4 (Baja)
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Estado</Label>
                    <Select 
                      value={formData.estatus} 
                      onValueChange={(v) => setFormData({ ...formData, estatus: v })}
                    >
                      <SelectTrigger data-testid="pago-estatus">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="programado">Programado</SelectItem>
                        <SelectItem value="pagado">Pagado</SelectItem>
                        <SelectItem value="vencido">Vencido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Recurrence Section */}
                  {!editingId && (
                    <div className="space-y-4 pt-4 border-t">
                      <Label className="text-base font-semibold">Recurrencia del Pago</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Tipo de Recurrencia</Label>
                          <Select 
                            value={formData.tipo_recurrencia} 
                            onValueChange={(v) => setFormData({ ...formData, tipo_recurrencia: v, fecha_fin_recurrencia: v === 'unico' ? '' : formData.fecha_fin_recurrencia })}
                          >
                            <SelectTrigger data-testid="pago-recurrencia">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unico">Pago Único</SelectItem>
                              <SelectItem value="mensual">Pago Mensual</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {formData.tipo_recurrencia === 'mensual' && (
                          <div className="space-y-2">
                            <Label>Finaliza en (Mes)</Label>
                            <Input
                              type="month"
                              value={formData.fecha_fin_recurrencia}
                              onChange={(e) => setFormData({ ...formData, fecha_fin_recurrencia: e.target.value })}
                              min={formData.fecha_vencimiento ? formData.fecha_vencimiento.substring(0, 7) : ''}
                              data-testid="pago-fecha-fin-recurrencia"
                            />
                          </div>
                        )}
                      </div>
                      {formData.tipo_recurrencia === 'mensual' && formData.fecha_fin_recurrencia && (
                        <div className="text-sm bg-blue-50 p-3 rounded border border-blue-200">
                          <p className="font-medium text-blue-800 mb-1">📅 Pago Recurrente</p>
                          <p className="text-blue-700">
                            Este pago se generará automáticamente cada mes hasta {new Date(formData.fecha_fin_recurrencia + '-01T00:00:00').toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}.
                          </p>
                          <p className="text-blue-600 text-xs mt-1">
                            Solo verás el pago del mes actual en el tablero. Los siguientes meses se crean automáticamente.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    <Save className="w-4 h-4 mr-2" />
                    Guardar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="page-content">
        {/* Summary Cards */}
        <div className="summary-cards-grid mb-6">
          <Card className="bg-yellow-50 border-yellow-200 summary-card">
            <CardContent className="p-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-yellow-600">Pendiente</p>
                  <p className="text-lg sm:text-2xl font-bold text-yellow-700 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {formatCurrency(totalPendiente)}
                  </p>
                </div>
                <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-red-50 border-red-200 summary-card">
            <CardContent className="p-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-red-600">Vencido</p>
                  <p className="text-lg sm:text-2xl font-bold text-red-700 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {formatCurrency(totalVencido)}
                  </p>
                </div>
                <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-primary/5 border-primary/20 summary-card col-span-2 sm:col-span-1">
            <CardContent className="p-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-primary">Total Pagos</p>
                  <p className="text-lg sm:text-2xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {pagosList.length}
                  </p>
                </div>
                <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-primary/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="filters-container">
          <Select value={filterCuentaId} onValueChange={setFilterCuentaId}>
            <SelectTrigger className="w-full sm:w-48" data-testid="filter-cuenta">
              <SelectValue placeholder="Todas las cuentas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las cuentas</SelectItem>
              {cuentasList.map((cuenta) => (
                <SelectItem key={cuenta.id} value={cuenta.id}>{cuenta.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedPeriodo} onValueChange={setSelectedPeriodo}>
            <SelectTrigger className="w-full sm:w-48" data-testid="filter-periodo">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              {periodoOptions.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterEstatus} onValueChange={setFilterEstatus}>
            <SelectTrigger className="w-full sm:w-48" data-testid="filter-estatus">
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pendiente">Pendiente</SelectItem>
              <SelectItem value="programado">Programado</SelectItem>
              <SelectItem value="vencido">Vencido</SelectItem>
              <SelectItem value="pagado">Pagado</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleArchivePeriod}
              className="text-muted-foreground hover:text-destructive"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archivar Período
            </Button>
          )}
        </div>

        {/* Mobile Card View */}
        <div className="data-table-mobile">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="skeleton h-32 rounded-xl"></div>
            ))
          ) : pagosList.length === 0 ? (
            <div className="empty-state">
              <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hay pagos registrados</p>
            </div>
          ) : (
            pagosList.map((pago) => {
              const statusConfig = getStatusConfig(pago.estatus);
              const StatusIcon = statusConfig.icon;
              
              return (
                <div 
                  key={pago.id} 
                  className={`table-mobile-card ${pago.estatus === 'vencido' ? 'vencido' : ''}`}
                  data-testid={`pago-card-${pago.id}`}
                >
                  <div className="table-mobile-card-header">
                    <div>
                      <div className="table-mobile-card-title flex items-center gap-2">
                        {pago.proveedor}
                        {pago.tipo_recurrencia === 'mensual' && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                            <Repeat className="w-3 h-3" />
                            Mensual
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{pago.concepto || 'Sin descripción'}</p>
                    </div>
                    <Badge className={statusConfig.class}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {statusConfig.label}
                    </Badge>
                  </div>
                  
                  <div className="table-mobile-card-row">
                    <label>Monto</label>
                    <span className="font-bold text-slate-900">{formatCurrency(pago.monto)}</span>
                  </div>
                  
                  <div className="table-mobile-card-row">
                    <label>Vencimiento</label>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-slate-400" />
                      {formatDate(pago.fecha_vencimiento)}
                    </span>
                  </div>
                  
                  <div className="table-mobile-card-row">
                    <label>Cuenta</label>
                    <Badge variant="outline" className="text-xs">{pago.cuenta_nombre}</Badge>
                  </div>
                  
                  <div className="table-mobile-card-row">
                    <label>Prioridad</label>
                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium priority-${pago.prioridad}`}>
                      P{pago.prioridad}
                    </div>
                  </div>
                  
                  {isAdmin && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => openEdit(pago)}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(pago.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Desktop Table View */}
        <Card className="table-container data-table-desktop">
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="pagos-table">
              <thead>
                <tr>
                  <th>Proveedor</th>
                  <th>Concepto</th>
                  <th>Vencimiento</th>
                  <th>Categoría</th>
                  <th>Cuenta</th>
                  <th>Estado</th>
                  <th>Prioridad</th>
                  <th className="text-right">Monto</th>
                  {isAdmin && <th className="w-24">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td colSpan={9}>
                        <div className="skeleton h-6 w-full"></div>
                      </td>
                    </tr>
                  ))
                ) : pagosList.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-12">
                      <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No hay pagos registrados</p>
                    </td>
                  </tr>
                ) : (
                  pagosList.map((pago) => {
                    const statusConfig = getStatusConfig(pago.estatus);
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <tr key={pago.id} className={pago.estatus === 'vencido' ? 'bg-red-50/50' : ''}>
                        <td className="font-medium">{pago.proveedor}</td>
                        <td className="text-muted-foreground">{pago.concepto || '-'}</td>
                        <td className="whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            {formatDate(pago.fecha_vencimiento)}
                          </div>
                        </td>
                        <td>
                          {pago.categoria_nombre ? (
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-2 h-2 rounded-full" 
                                style={{ 
                                  backgroundColor: categoriasList.find(c => c.id === pago.categoria_id)?.color 
                                }}
                              />
                              {pago.categoria_nombre}
                            </div>
                          ) : '-'}
                        </td>
                        <td>
                          <Badge variant="outline">{pago.cuenta_nombre}</Badge>
                        </td>
                        <td>
                          {isAdmin ? (
                            <Select 
                              value={pago.estatus} 
                              onValueChange={(v) => handleStatusChange(pago.id, v)}
                            >
                              <SelectTrigger className="w-32 h-8">
                                <SelectValue>
                                  <Badge className={statusConfig.class}>
                                    <StatusIcon className="w-3 h-3 mr-1" />
                                    {statusConfig.label}
                                  </Badge>
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pendiente">Pendiente</SelectItem>
                                <SelectItem value="programado">Programado</SelectItem>
                                <SelectItem value="pagado">Pagado</SelectItem>
                                <SelectItem value="vencido">Vencido</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={statusConfig.class}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          )}
                        </td>
                        <td>
                          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium priority-${pago.prioridad}`}>
                            P{pago.prioridad}
                          </div>
                        </td>
                        <td className="text-right font-bold">
                          {formatCurrency(pago.monto)}
                        </td>
                        {isAdmin && (
                          <td>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8"
                                onClick={() => openEdit(pago)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => handleDelete(pago.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
